import { useContext, useEffect, useRef, useState } from "react";
import { Button } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useRecoilValue } from "recoil";
import { AppContext } from "../../AppContext";
import { memberState } from "../../state/atoms";
import { env } from "../../env/env";
import BrandTemplateApi, {
  BrandTemplateDto,
  BrandTemplateProductType,
} from "../../api/BrandTemplateApi";
import RendererThumb from "../common/RendererThumb";
import "./BrandTemplateCarousel.scss";

enum Direction {
  Left,
  Right,
}

const ScrollStep = 500;
const NAME_CHAR_LIMIT = 18;

const PRODUCT_SIZE_LABEL: Record<BrandTemplateProductType, string> = {
  MENU_LETTER: "8.5 x 11",
  MENU_LEGAL: "8.5 x 14",
  MENU_TABLOID: "11 x 17",
  MENU_A4: "8.27 x 11.69",
  MENU_CUSTOM_SIZE: "",
  DMB_LANDSCAPE: "16 x 9",
  DMB_PORTRAIT: "9 x 16",
};

function sizeLabel(t: BrandTemplateDto): string {
  if (t.productType === "MENU_CUSTOM_SIZE" && t.width && t.height) {
    const unit = (t.sizeUnit || "in").toString().toLowerCase();
    return `${t.width} x ${t.height} ${unit}`;
  }
  return PRODUCT_SIZE_LABEL[t.productType] || "";
}

function truncateName(name: string): string {
  return name.length > NAME_CHAR_LIMIT
    ? `${name.slice(0, NAME_CHAR_LIMIT).trimEnd()}…`
    : name;
}

export interface BrandTemplateCarouselProps {
  onLoaded?: (hasTemplates: boolean) => void;
}

export default function BrandTemplateCarousel({ onLoaded }: BrandTemplateCarouselProps) {
  const { location } = useContext(AppContext);
  const member = useRecoilValue(memberState);

  const [templates, setTemplates] = useState<BrandTemplateDto[]>([]);
  const [loaded, setLoaded] = useState(false);

  const carousel = useRef<HTMLUListElement>(null);
  const [left, setLeft] = useState(false);
  const [right, setRight] = useState(true);

  const isAdminOrOwner = member.owner || member.role === "Admin";

  useEffect(() => {
    if (!location?.internalId) return;
    let cancelled = false;
    setLoaded(false);
    BrandTemplateApi.listByLocation(location.internalId)
      .then((data) => {
        if (cancelled) return;
        const list = data || [];
        setTemplates(list);
        onLoaded?.(list.length > 0);
      })
      .catch(() => {
        if (cancelled) return;
        setTemplates([]);
        onLoaded?.(false);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.internalId]);

  useEffect(() => {
    const current = carousel.current;
    if (!current) return;

    const update = () => {
      const width = current.clientWidth;
      const fullWidth = current.scrollWidth;
      const scrollLeft = current.scrollLeft;
      setLeft(scrollLeft > 0);
      setRight(width + scrollLeft + 10 < fullWidth);
    };

    update();
    current.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      current.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [templates]);

  function scroll(dir: Direction) {
    const current = carousel.current;
    if (!current) return;
    current.scroll({
      top: 0,
      left:
        dir === Direction.Left
          ? current.scrollLeft - ScrollStep
          : current.scrollLeft + ScrollStep,
      behavior: "smooth",
    });
  }

  function openTemplate(template: BrandTemplateDto) {
    if (!template.id) return;
    window.location.href =
      env.timberContextPath +
      "/design?parentId=" +
      encodeURIComponent(template.id) +
      "&fromSample=true";
  }

  if (!loaded || templates.length === 0) return null;

  return (
    <section className="brand-template-carousel">
      <header className="brand-template-carousel-header">
        <h3>Start new design</h3>
        {isAdminOrOwner && (
          <a
            className="manage-brand-link"
            href={`${env.dashboardPath}/account/brand-management`}
          >
            Manage brand
          </a>
        )}
      </header>
      <div className="brand-template-carousel-body">
        {left && (
          <Button className={"bt btn-left"} onClick={() => scroll(Direction.Left)}>
            <LeftOutlined />
          </Button>
        )}
        <ul ref={carousel}>
          {templates.map((template, idx) => (
            <li key={template.id || idx} className="brand-template-card">
              <span
                className="brand-template-thumb"
                onClick={() => openTemplate(template)}
              >
                <RendererThumb
                  id={template.id}
                  alt={template.name}
                  loading="lazy"
                  className="brand-template-thumb-img"
                  fallback={
                    <span className="brand-template-thumb-fallback">
                      {template.name}
                    </span>
                  }
                />
                <span className="brand-template-use">Use this</span>
              </span>
              <div className="brand-template-meta">
                {(() => {
                  const displayName = template.name || `Menu ${idx + 1}`;
                  const size = sizeLabel(template);
                  return (
                    <>
                      <p
                        className="brand-template-name"
                        title={
                          displayName.length > NAME_CHAR_LIMIT
                            ? displayName
                            : undefined
                        }
                      >
                        {truncateName(displayName)}
                      </p>
                      {size && <span className="brand-template-size">{size}</span>}
                    </>
                  );
                })()}
              </div>
            </li>
          ))}
        </ul>
        {right && (
          <Button className={"bt btn-right"} onClick={() => scroll(Direction.Right)}>
            <RightOutlined />
          </Button>
        )}
      </div>
    </section>
  );
}
